import { motion } from 'framer-motion';
import { CheckCircle, Clock, Brain } from '../icons';
import type { CoachRecentCompletion } from '../../lib/coach-dashboard';

interface CoachRecentCompletionsProps {
  completions: CoachRecentCompletion[];
  locale?: string;
}

function formatDate(dateStr: string, tr?: boolean): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (hours < 1) return tr ? 'Az önce' : 'Just now';
  if (hours < 24) return tr ? `${hours} saat önce` : `${hours}h ago`;
  if (days < 7) return tr ? `${days} gün önce` : `${days}d ago`;
  return date.toLocaleDateString(tr ? 'tr-TR' : 'en-GB', { day: 'numeric', month: 'short' });
}

export function CoachRecentCompletions({ completions, locale }: CoachRecentCompletionsProps) {
  const tr = locale === 'tr';
  if (completions.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">{tr ? 'Son Tamamlamalar' : 'Recent Completions'}</h3>
        <p className="text-text-sm text-gray-40 text-center py-6">
          {tr ? 'Henüz tamamlanan değerlendirme yok' : 'No completed assessments yet'}
        </p>
      </div>
    );
  }

  return (
    <div className="freud-card-elevated p-6">
      <h3 className="text-text-xl text-gray-80 mb-4">{tr ? 'Son Tamamlamalar' : 'Recent Completions'}</h3>
      <div className="space-y-1">
        {completions.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-brand-5 transition-colors"
          >
            <div className="p-1.5 bg-success-5 rounded-lg flex-shrink-0">
              <CheckCircle className="w-4 h-4 text-success-40" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-text-sm text-gray-80 font-medium truncate">
                {item.respondentName}
              </p>
            </div>
            {item.hasAiAnalysis && (
              <div className="p-1 bg-teal-10 rounded-md flex-shrink-0" title="AI analysis generated">
                <Brain className="w-3.5 h-3.5 text-teal-50" />
              </div>
            )}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Clock className="w-3 h-3 text-gray-30" />
              <span className="text-text-xs text-gray-40">
                {formatDate(item.completedAt, tr)}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
